import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudSun, Play, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Settings ▸ Weather Backfill.
 *
 * One-shot admin action that seeds `weather_hourly` + refreshes
 * `monitoring_15min.estimated_kwh` for every project with lat/lng across
 * the trailing N months (default 12). Powers the public share
 * sparklines + actual-vs-estimated variance charts.
 *
 * The endpoint runs synchronously so the admin sees a per-project
 * summary — larger portfolios (>20 projects) may want to bump the
 * `max_projects` cap and grab a coffee.
 */
export default function WeatherBackfillSection() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [status, setStatus] = useState(null);
  const [months, setMonths] = useState(12);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/weather-backfill-status");
      setStatus(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { if (canManage) load(); }, [canManage]);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/settings/weather-backfill-all",
        { lookback_days: Math.round(months * 30.44) });
      setResult(data);
      toast.success(`Backfill complete — ${data.ok_count}/${data.total_projects} projects OK`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backfill failed");
    } finally { setBusy(false); }
  };

  if (!canManage) return null;
  const lastRun = status?.last_manual_run || status?.last_scheduled_run;

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="weather-backfill-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CloudSun size={14} style={{ color: "var(--accent-hover)" }} />
            <div className="label">Weather backfill</div>
          </div>
          <p className="muted text-xs">
            Seed weather-based forecast (`estimated_kwh`) for every project with coordinates.
            Populates the investor share sparklines + actual-vs-estimate variance charts.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="block">
            <span className="label block mb-1 text-[10px]">Months</span>
            <select className="input-swiss py-1 text-sm" value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="wb-months">
              <option value={3}>3</option>
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
            </select>
          </label>
          <button className="btn-accent" onClick={run} disabled={busy || !status?.eligible_projects}
                  data-testid="wb-run-btn" title={status?.eligible_projects ? "" : "No projects have lat/lng set"}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {busy ? "Running…" : "Run backfill"}
          </button>
        </div>
      </div>

      {status && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <StatTile label="Eligible projects" value={`${status.eligible_projects} / ${status.total_projects}`} />
          <StatTile
            label="Last run"
            value={lastRun ? new Date(lastRun.started_at || lastRun.finished_at || "").toLocaleDateString() : "never"}
          />
          <StatTile
            label="Hours seeded"
            value={lastRun?.total_hours_written != null ? lastRun.total_hours_written.toLocaleString() : "—"}
          />
        </div>
      )}

      {result && (
        <div className="mt-4 border border-[var(--line)] rounded-md overflow-hidden" data-testid="wb-result">
          <div className="px-4 py-2 bg-[var(--bg-2)] flex items-center gap-2 text-xs">
            <CheckCircle2 size={13} className="text-[var(--accent-hover)]" />
            <span><strong>{result.ok_count}</strong> OK · <strong>{result.error_count}</strong> errors · <strong>{result.skipped_count}</strong> skipped</span>
            <span className="muted ml-2">{result.total_hours_written?.toLocaleString?.() || 0} hours seeded · {result.total_monitoring_rows_updated?.toLocaleString?.() || 0} monitoring rows refreshed</span>
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="data-table w-full">
              <thead>
                <tr><th>Project</th><th>Status</th><th className="text-right">Hours</th><th className="text-right">Rows</th></tr>
              </thead>
              <tbody>
                {(result.results || []).map(r => (
                  <tr key={r.project_id} data-testid={`wb-row-${r.project_id}`}>
                    <td className="truncate max-w-[240px]">{r.name || r.project_id}</td>
                    <td>
                      <span className={`tag text-[10px] ${r.status === "ok" ? "ok" : r.status === "error" ? "danger" : "warn"}`}>
                        {r.status}
                      </span>
                      {r.reason && <span className="ml-2 text-[11px] muted italic">{r.reason}</span>}
                      {r.error && <span className="ml-2 text-[11px] text-[var(--danger,#D64545)] truncate max-w-[220px] inline-block align-middle">{r.error}</span>}
                    </td>
                    <td className="text-right font-mono text-xs">{r.hours_written ?? "—"}</td>
                    <td className="text-right font-mono text-xs">{r.monitoring_rows_updated ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="p-3 border border-[var(--line)] rounded">
      <div className="text-[10px] uppercase tracking-wider muted">{label}</div>
      <div className="font-mono text-sm font-semibold mt-1">{value}</div>
    </div>
  );
}
